import { useEffect, useState } from 'react';
import { useElementSize } from '@fiverr-private/hooks';
import { useTabContext } from '../../../TabContext';
import type { IndicatorProps } from '../types';
import type { TabContextType } from '../../../types';
import { isSegmentedControlVariant } from '../../../helpers';

const BORDER_LEFT_WIDTH = 1;
const getTabBoundingClientRect = (
    focusedTab: TabContextType['focusedTab'],
    tabListRef: IndicatorProps['tabListRef'],
    isHorizontal: boolean
) => {
    if (!focusedTab || !focusedTab.current || !tabListRef.current) {
        return {};
    }
    const side = isHorizontal ? 'left' : 'top';
    const tabRect = focusedTab?.current.getBoundingClientRect();
    const containerRect = tabListRef.current.getBoundingClientRect();
    const position =
        (side === 'left'
            ? tabRect.left - containerRect.left + tabListRef.current.scrollLeft
            : tabRect.top - containerRect.top) + tabListRef.current.scrollTop;

    return {
        position,
        width: tabRect.width,
        height: tabRect.height,
    };
};

const getAnimationStyles = (
    focusedTab: TabContextType['focusedTab'],
    orientation: IndicatorProps['orientation'],
    variant: IndicatorProps['variant'],
    tabListRef: IndicatorProps['tabListRef']
) => {
    if (!focusedTab || !focusedTab.current) {
        return {};
    }
    const isHorizontal = orientation === 'horizontal';
    const { width, height, position } = getTabBoundingClientRect(focusedTab, tabListRef, isHorizontal);

    if (variant === 'default') {
        return {
            ...(isHorizontal ? { width } : { height }),
            transform: `translate${isHorizontal ? 'X' : 'Y'}(${position}px)`,
        };
    }

    if (isSegmentedControlVariant(variant)) {
        return {
            width,
            height,
            transform: `translate${isHorizontal ? 'X' : 'Y'}(${(position ?? 0) - BORDER_LEFT_WIDTH}px)`,
        };
    }
};

const useIndicatorStyles = (orientation: IndicatorProps['orientation'], tabListRef: IndicatorProps['tabListRef']) => {
    const { focusedTab, variant } = useTabContext();
    const [styles, setStyles] = useState({});
    const size = useElementSize(tabListRef?.current);

    useEffect(() => {
        setStyles(getAnimationStyles(focusedTab, orientation, variant, tabListRef) || {});
    }, [focusedTab, orientation, variant, tabListRef, size]);

    return styles;
};

export default useIndicatorStyles;
